import {
  Badge,
  BodyText,
  Button,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@utilitywarehouse/hearth-react';
import {
  ExpandSmallIcon,
  TickSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';
import { FC, ReactNode, useState } from 'react';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { UpdatingModal } from '@components/UpdatingModal';
import { paths } from '@constants/paths';
import { TUTORIAL_CONTROLS_SEEN_KEY } from '@constants/storageKeys';
import { InviteDialog } from '@features/Remote/Invite/InviteDialog';
import {
  Appointment,
  AppointmentParticipantType,
  ControlGivenEvent,
  ControlRequestedEvent,
} from '@generated/gql/graphql';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useAppointmentControlContext } from '@providers/AppointmentProvider/AppointmentControlContext';
import {
  useAppointmentUserId,
  useAppointmentUserType,
} from '@store/appointment/selectors';

import { TutorialPopup } from '../Invite/TutorialPopup';
import { ControlRequestDialog } from './Modals/ControlRequestDialog';
import { EndAppointmentDialog } from './Modals/EndAppointmentDialog';
import { OptionsMenu } from './OptionsMenu';
import './styles.scss';
import { useControlFeedbackModals } from './useControlFeedbackModals';
import { useScreenControl } from './useScreenControl';

interface SessionControls {
  children: ReactNode;
  appointment: Appointment;
  controlRequest?: ControlRequestedEvent | null;
  endAppointment: () => void;
  controlGiven?: ControlGivenEvent | null;
  hasControl: boolean | null;
}

export const SessionControlsUnwrapped: FC<SessionControls> = ({
  children,
  appointment,
  controlRequest,
  endAppointment,
  controlGiven,
  hasControl,
}) => {
  const { pathname } = useLocation();

  const sessionControlsRef = useRef<HTMLDivElement | null>(null);

  const isPresentationPage = pathname === '/presentation';

  const userId = useAppointmentUserId();
  const userType = useAppointmentUserType();

  const [screenControlOpen, setScreenControlOpen] = useState(false);
  const {
    inControl,
    controller,
    participants,
    controllerColour,
    setController: setControllerHook,
    denyControlRequest,
    handleOverlayclick,
    endDialog,
    infoDialog,
  } = useScreenControl({
    appointment,
    userId: userId ?? '',
    controlRequest: controlRequest ?? undefined,
  });

  const setController = (participant: (typeof participants)[0]) => {
    setScreenControlOpen(false);
    setControllerHook(participant);
  };

  const { dialogProps } = useControlFeedbackModals({
    controlRequest,
    controlGiven,
    hasControl,
    userId,
  });

  return (
    <>
      <div className="session_controls">
        <div
          className={`session_controls__border session_controls__border--${controllerColour}`}
        />

        <div className="session_controls__ControlBadgeWrapper">
          <Badge
            size="sm"
            variant="subtle"
            colorScheme={controllerColour}
            className="session_controls__ControlBadge"
          >
            {controller.name} has control of the screen
          </Badge>
        </div>
        <div className="session_controls-header">
          <div className="session_controls-header-wrapper wrapper">
            <div className="session_controls-header-container">
              <div className="session_controls-header-controller">
                <div
                  ref={sessionControlsRef}
                  className="session_controls-header-controller__select"
                >
                  <Menu modal={false}>
                    <MenuTrigger>
                      <Button
                        variant="outline"
                        size="sm"
                        inverted
                        colorScheme="functional"
                        disabled={appointment.isProtectedPath}
                      >
                        Change screen control <ExpandSmallIcon />
                      </Button>
                    </MenuTrigger>
                    {!appointment.isProtectedPath && (
                      <MenuContent placement="bottomLeft">
                        {participants.map((participant) => {
                          return (
                            <MenuItem
                              onSelect={() => setController(participant)}
                              key={participant.id}
                              disabled={
                                participant.id === userId &&
                                participant.id === controller.id
                              }
                            >
                              {participant.id === userId
                                ? 'Me'
                                : participant.name}
                              {participant.id === controller.id && (
                                <TickSmallIcon />
                              )}
                            </MenuItem>
                          );
                        })}
                      </MenuContent>
                    )}
                  </Menu>
                </div>
              </div>
              {screenControlOpen && appointment.isProtectedPath && (
                <BodyText className="session_controls-header-controller__protected">
                  {inControl ? 'You' : (controller.name ?? 'They')} must keep
                  control of this screen
                </BodyText>
              )}
            </div>

            <OptionsMenu
              onEnd={() => endDialog.setOpen(true)}
              onOpenInfo={() => infoDialog.setOpen(true)}
            />
          </div>
        </div>
        <div
          onClick={handleOverlayclick}
          className={clsx({
            'session_controls__overlay--active': screenControlOpen,
            'session_controls__overlay--follower':
              !inControl && !isPresentationPage,
          })}
        ></div>
        {controlRequest && controlRequest.toParticipant.id === userId && (
          <ControlRequestDialog
            controlRequest={controlRequest}
            setController={setController}
            deny={denyControlRequest}
          />
        )}
        <UpdatingModal {...dialogProps} />
        <EndAppointmentDialog end={endAppointment} />
        {userType !== AppointmentParticipantType.Customer && (
          <TutorialPopup
            anchorRef={sessionControlsRef}
            text="You can change who has control of the screen here during your appointment."
            tutorialKey={TUTORIAL_CONTROLS_SEEN_KEY}
          />
        )}
        <InviteDialog />
      </div>

      {children}
    </>
  );
};

export const SessionControls = ({ children }: { children: ReactNode }) => {
  const { appointment, endAppointment, isSyncedAppointment, hasControl } =
    useAppointmentContext();
  const { controlRequest, controlGiven } = useAppointmentControlContext();
  const { pathname } = useLocation();

  // prevent appointment controls from appearing on defined routes
  // where an appointment exists but syncing is not yet required.
  const ignorePaths = [paths.remoteSetup];

  if (appointment && isSyncedAppointment && !ignorePaths.includes(pathname)) {
    return (
      <SessionControlsUnwrapped
        appointment={appointment}
        controlRequest={controlRequest}
        endAppointment={endAppointment}
        controlGiven={controlGiven}
        hasControl={hasControl}
      >
        {children}
      </SessionControlsUnwrapped>
    );
  }

  return children;
};
